/**
 * NotFound — the catch-all 404 (rebuilt 2026-08, counter revamp).
 *
 * WHAT IT WAS. The generated-404 template: a centred icon tile, an
 * uppercase kicker ("Ошибка 404"), a giant display-font 404, a title, a grey
 * sentence, and a button. Six stacked centred elements, five of which said the
 * same thing, and the one fact the user actually needs — WHICH address failed
 * — was nowhere on the page. The kicker is a hard-banned pattern in this
 * revamp; it is gone rather than restyled.
 *
 * WHAT IT IS NOW. A counter slip that came back stamped. The code sits in the
 * instrument face (`.g-data`) because an HTTP status IS a code, exactly like a
 * stall code; the address that failed is printed on its own ruled row as the
 * datum it is, wrapping rather than truncating so a long path stays readable;
 * and the page offers the two moves that actually help someone who mistyped a
 * URL in a market — go home, or ask the assistant. `useLocation` supplies the
 * path, so the slip reports what happened instead of describing 404s in
 * general.
 *
 * The shyrdak motif is given real presence (0.1, up from a barely-there 0.06)
 * behind the whole surface, which is what makes this read as GardEn's own
 * empty page rather than as any framework's.
 *
 * Testids `not-found` and `not-found-home` are unchanged. The route is mounted
 * OUTSIDE AppShell, so this surface carries its own wordmark and theme toggle.
 */
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, MessageCircle } from "lucide-react";

import { MotifSurface, Stagger, StaggerItem, pressClass } from "@/components/motion";
import { ThemeToggle } from "@/components/ThemeToggle";
import { iconProps } from "@/lib/icons";

export function NotFound(): ReactElement {
  const { t } = useTranslation("common");
  const location = useLocation();
  const path = `${location.pathname}${location.search}`;

  return (
    <MotifSurface
      tone="primary"
      opacity={0.1}
      className="min-h-dvh bg-background pb-[max(1.5rem,env(safe-area-inset-bottom))]"
    >
      <header className="mx-auto flex w-full max-w-2xl items-center justify-between gap-3 px-5 pt-[max(0.875rem,env(safe-area-inset-top))]">
        {/* The app bar's logotype, in its own face (`font-wordmark`). */}
        <Link
          to="/"
          className="rounded-[10px] px-1 font-wordmark text-[1.0625rem] font-bold tracking-[-0.04em] text-primary transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          {t("appName")}
        </Link>
        <ThemeToggle />
      </header>

      <main
        data-testid="not-found"
        className="mx-auto flex min-h-[70dvh] w-full max-w-2xl flex-col justify-center px-5 py-10"
      >
        <Stagger className="flex flex-col items-start">
          {/* The stamp. `.g-data` because a status code is a code — the same
              instrument face a stall code or a price is set in. It is NOT
              aria-hidden any more: the old page hid it because the kicker
              above already read "Ошибка 404"; with the kicker gone, "404" is
              the one thing a screen-reader user should hear first. */}
          <StaggerItem className="g-data text-[3.5rem] font-bold leading-none text-[color:var(--g-ink-2)] sm:text-[4.5rem]">
            {t("notFound.code")}
          </StaggerItem>

          <StaggerItem
            as="h1"
            className="mt-4 font-display text-2xl font-bold leading-tight text-foreground sm:text-[2rem]"
          >
            {t("notFound.title")}
          </StaggerItem>

          <StaggerItem
            as="p"
            className="mt-2.5 max-w-prose text-[0.9375rem] leading-relaxed text-[color:var(--g-ink-2)]"
          >
            {t("notFound.description")}
          </StaggerItem>

          {/* The one fact the old page never stated: what was actually asked
              for. Drawn rules above and below so it reads as a line off a
              slip, not as a card. */}
          <StaggerItem className="mt-7 w-full border-y border-[color:var(--g-rule)] py-3">
            <span className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-4">
              <span className="shrink-0 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-[color:var(--g-ink-3)]">
                {t("notFound.path")}
              </span>
              <span className="g-data min-w-0 break-all text-sm text-foreground">
                {path}
              </span>
            </span>
          </StaggerItem>

          <StaggerItem className="mt-7 flex w-full flex-col gap-2.5 sm:flex-row">
            <Link
              to="/"
              data-testid="not-found-home"
              className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-[10px] bg-primary px-5 text-sm font-semibold text-[color:var(--g-on-primary)] transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ${pressClass("cta")}`}
            >
              {t("notFound.back")}
              <ArrowRight {...iconProps("sm")} />
            </Link>
            <Link
              to="/chat"
              data-testid="not-found-assistant"
              className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-[10px] border border-[color:var(--g-rule)] px-5 text-sm font-semibold text-foreground transition-colors hover:bg-[color:var(--g-sunken)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ${pressClass("cta")}`}
            >
              <MessageCircle {...iconProps("sm")} />
              {t("notFound.assistant")}
            </Link>
          </StaggerItem>
        </Stagger>
      </main>
    </MotifSurface>
  );
}

export default NotFound;
